var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useCallback } from 'react';
import { routesConfig } from '../components/signup-funnel/signup-funnel-ts/create-home-funnel/config';
import { cloneDeep, get, set, toPath } from 'lodash';
import Home from '../models/Home';
import i18n from '../i18n';
export var useHomeForm = function (initialData, homes) {
    var _a = useState(transformApiToForm(initialData) || {}), formData = _a[0], setFormData = _a[1];
    var homesAlreadySavedWithSameAddress = homes
        .filter(function (home) { return home.get('location'); })
        .filter(function (home) {
        var _a, _b;
        return home.get('location').latitude === ((_a = formData.location) === null || _a === void 0 ? void 0 : _a.latitude) &&
            home.get('location').longitude === ((_b = formData.location) === null || _b === void 0 ? void 0 : _b.longitude);
    });
    var normalizePath = function (path) {
        return toPath(path).join('.');
    };
    var updateField = useCallback(function (path, value) {
        setFormData(function (prev) {
            var newData = cloneDeep(prev);
            set(newData, normalizePath(path), value);
            return newData;
        });
    }, []);
    var validateDuplicatedAddress = useCallback(function () {
        if (!homesAlreadySavedWithSameAddress.length) {
            return { isValid: true };
        }
        var homeWithMaxCompletionRate = homesAlreadySavedWithSameAddress.reduce(function (maxHome, currentHome) {
            var currentRate = currentHome.get('completion_rate');
            var maxRate = maxHome.get('completion_rate');
            return currentRate > maxRate ? currentHome : maxHome;
        });
        if ((formData === null || formData === void 0 ? void 0 : formData.completion_rate) <= homeWithMaxCompletionRate.get('completion_rate') &&
            (formData === null || formData === void 0 ? void 0 : formData.id) !== homeWithMaxCompletionRate.id) {
            return {
                isValid: false,
                duplicateHomes: [homeWithMaxCompletionRate]
            };
        }
        return {
            isValid: true
        };
    }, []);
    var validateStep = useCallback(function (stepId, partId, type) {
        var _a, _b, _c;
        var stepConfig = (_a = routesConfig[partId]) === null || _a === void 0 ? void 0 : _a.steps[stepId];
        if (!stepConfig) {
            return { isValid: true };
        }
        if (stepConfig.required && stepConfig.fieldName) {
            if (type === 'address' && homes) {
                if (homesAlreadySavedWithSameAddress.length > 0) {
                    return {
                        isValid: false,
                        duplicateHomes: homesAlreadySavedWithSameAddress
                    };
                }
                if (formData.overridden_address === 1 && (!formData.address || formData.address === '')) {
                    return {
                        isValid: false,
                        errorMessage: i18n.t('home:enter_full_address_manually'),
                        fieldNames: ['address']
                    };
                }
                if ((formData.overridden_address === 1 &&
                    !((_b = formData.location) === null || _b === void 0 ? void 0 : _b.latitude) &&
                    !((_c = formData.location) === null || _c === void 0 ? void 0 : _c.longitude)) ||
                    !formData.location_id) {
                    return {
                        isValid: false,
                        errorMessage: i18n.t('home:enter_address_or_gps'),
                        fieldNames: ['location']
                    };
                }
            }
            var fieldNames = Array.isArray(stepConfig.fieldName)
                ? stepConfig.fieldName
                : [stepConfig.fieldName];
            var result = validateFields(fieldNames, stepConfig.errorMessage);
            if (!result.isValid)
                return result;
        }
        if (stepConfig.options) {
            for (var _i = 0, _d = stepConfig.options; _i < _d.length; _i++) {
                var option = _d[_i];
                if (option.required && option.fieldName) {
                    var result = validateFields([option.fieldName], option.errorMessage);
                    if (!result.isValid)
                        return result;
                }
            }
        }
        if (stepConfig.validationRules) {
            for (var _e = 0, _f = stepConfig.validationRules; _e < _f.length; _e++) {
                var rule = _f[_e];
                var result = validateFields(rule.fieldNames, rule.errorMessage, rule.rule);
                if (!result.isValid)
                    return result;
            }
        }
        return { isValid: true };
    }, [formData]);
    var validateFields = function (fieldNames, errorMessage, rule) {
        for (var _i = 0, fieldNames_1 = fieldNames; _i < fieldNames_1.length; _i++) {
            var field = fieldNames_1[_i];
            var value = get(formData, normalizePath(field));
            if ((!value || value === '') && rule === undefined) {
                return {
                    isValid: false,
                    errorMessage: errorMessage || 'Ce champ est obligatoire',
                    fieldNames: [field]
                };
            }
            if (rule) {
                if (rule === 'positiveNumber' && value <= 0) {
                    return {
                        isValid: false,
                        errorMessage: errorMessage || 'Ce champ doit être un nombre positif',
                        fieldNames: [field]
                    };
                }
                if (rule === 'maxLength100' && value && value.length > Home.HOME_LIMIT_TITLE_CHARACTERS) {
                    return {
                        isValid: false,
                        errorMessage: errorMessage || 'Le titre ne doit pas dépasser 100 caractères',
                        fieldNames: [field]
                    };
                }
                if (rule === 'requiredOneOf') {
                    var hasAtLeastOneValue = fieldNames.some(function (field) {
                        var value = get(formData, normalizePath(field));
                        return value && value !== '';
                    });
                    if (!hasAtLeastOneValue) {
                        return {
                            isValid: false,
                            fieldNames: fieldNames
                        };
                    }
                }
            }
        }
        return { isValid: true };
    };
    var validateBedsAccommodations = function () {
        var value = get(formData, normalizePath('detail'));
        var status = 'info';
        var size = value.size;
        var sizeUnit = value.size_unit;
        var bedroomNb = value.bedroom_nb || 0;
        var totalSleep = value.single_bed +
            value.double_bed * 2 +
            value.big_double_bed * 2 +
            value.single_bed_up +
            value.double_bed_up * 2 +
            value.children_bed +
            value.children_bed_up +
            value.baby_bed +
            value.baby_bed_up;
        var totalAdults = value.single_bed +
            value.double_bed * 2 +
            value.big_double_bed * 2 +
            value.single_bed_up +
            value.double_bed_up * 2;
        var totalChildren = value.children_bed + value.children_bed_up;
        var totalBabies = value.baby_bed + value.baby_bed_up;
        if (totalSleep > 0 && totalAdults === 0) {
            status = 'error';
        }
        if (totalSleep === 0) {
            return null;
        }
        var ratio = sizeUnit === Home.METRIC_SQFT ? 86.11 : 8; // 1 bed per 8 m² or 86.11 ft²
        var totalSleepBySurface = Math.floor(size / ratio);
        // min size for apply warning
        var minSize = sizeUnit === Home.METRIC_SQFT ? 91.5 : 8.5;
        var maxGuestsForBedrooms = bedroomNb * 4 + 2;
        var isTooSmallToWarn = size < minSize;
        var isStudio = bedroomNb === 0;
        var totalMaxGuests = Math.min(maxGuestsForBedrooms, totalSleepBySurface);
        if (((bedroomNb > 0 && totalSleep > maxGuestsForBedrooms) || totalSleepBySurface < totalSleep) &&
            totalAdults > 0 &&
            !isTooSmallToWarn &&
            !isStudio) {
            status = 'warning';
        }
        return {
            status: status,
            totalMaxGuests: totalMaxGuests,
            totalAdults: totalAdults,
            totalChildren: totalChildren,
            totalBabies: totalBabies
        };
    };
    return {
        formData: formData,
        updateField: updateField,
        validateStep: validateStep,
        validateDuplicatedAddress: validateDuplicatedAddress,
        validateBedsAccommodations: validateBedsAccommodations
    };
};
export var transformApiToForm = function (apiData) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    if (!(apiData === null || apiData === void 0 ? void 0 : apiData.attributes)) {
        return {};
    }
    var attributes = apiData.attributes;
    var formData = {
        type: attributes.type,
        shared_part: attributes.shared_part
    };
    if (attributes.detail) {
        var defaultDetail = {
            residence_type: attributes.detail.residence_type,
            size: attributes.detail.size || 0,
            size_unit: attributes.detail.size_unit || '',
            bedroom_nb: attributes.detail.bedroom_nb || 0,
            bathroom_nb: attributes.detail.bathroom_nb || 0,
            single_bed: attributes.detail.single_bed || 0,
            single_bed_up: attributes.detail.single_bed_up || 0,
            double_bed: attributes.detail.double_bed || 0,
            big_double_bed: attributes.detail.big_double_bed || 0,
            double_bed_up: attributes.detail.double_bed_up || 0,
            children_bed: attributes.detail.children_bed || 0,
            children_bed_up: attributes.detail.children_bed_up || 0,
            baby_bed: attributes.detail.baby_bed || 0,
            baby_bed_up: attributes.detail.baby_bed_up || 0,
            location_type: attributes.detail.location_type || ''
        };
        formData.detail = defaultDetail;
    }
    var hasAddress = attributes.address;
    var hasLocation = ((_a = attributes.location) === null || _a === void 0 ? void 0 : _a.latitude) && ((_b = attributes.location) === null || _b === void 0 ? void 0 : _b.longitude);
    if (hasAddress || hasLocation) {
        if (hasAddress) {
            formData.address = attributes.address;
        }
        if (hasLocation) {
            formData.location = attributes.location;
        }
    }
    var hasFeatures = attributes.feature;
    if (hasFeatures) {
        formData.feature = __assign({}, attributes.feature);
    }
    if ((_c = attributes.descriptions) === null || _c === void 0 ? void 0 : _c.length) {
        formData.descriptions = __assign(__assign({}, attributes.descriptions), { good_feature: attributes.descriptions.good_feature || '', good_place: attributes.descriptions.good_place || '' });
    }
    if ((_d = attributes.images) === null || _d === void 0 ? void 0 : _d.length) {
        formData.images = attributes.images;
    }
    formData.is_accessible_for_prm = (_e = attributes.is_accessible_for_prm) !== null && _e !== void 0 ? _e : false;
    formData.has_professional_cleaner = (_f = attributes.has_professional_cleaner) !== null && _f !== void 0 ? _f : false;
    formData.professional_cleaning_contribution = (_g = attributes.professional_cleaning_contribution) !== null && _g !== void 0 ? _g : null;
    formData.professional_cleaning_amount = (_h = attributes.professional_cleaning_amount) !== null && _h !== void 0 ? _h : null;
    formData.professional_cleaning_currency = (_j = attributes.professional_cleaning_currency) !== null && _j !== void 0 ? _j : null;
    if (attributes.legal_registration_required) {
        formData.legal_registration_required = attributes.legal_registration_required;
    }
    if (attributes.legal_registration_homes) {
        formData.legal_registration_homes = attributes.legal_registration_homes;
    }
    if (attributes.translated_admins) {
        formData.translated_admins = attributes.translated_admins;
    }
    if (attributes.completion_rate) {
        formData.completion_rate = attributes.completion_rate;
    }
    if (attributes.id) {
        formData.id = attributes.id;
    }
    return formData;
};
